// The Chats surface's client (module 09 M16; module 10 U85; B599).
//
// MIRROR OF ChatsView and TeamsThreadView in apps/api/src/routes/chats.ts, field for field. A copy
// rather than an import, as meetings/api.ts is, because the two packages do not depend on one another.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';
import type { TeamsConsentStatus } from '../onboarding/api.js';

// Server copy: apps/api/src/chats/act.ts (decoded image bytes).
export const MAX_INLINE_IMAGE_BYTES = 4 * 1024 * 1024;

export interface ChatPerson { name: string; address: string | null; external: boolean }

export interface ChatRow {
  /** Where the conversation lives: Cobrain's own messenger, or Teams. */
  source: 'cobrain' | 'teams';
  /** The Cobrain conversation's id, or the Teams thread's. */
  id: string;
  title: string | null;
  people: ChatPerson[];
  /** Somebody in the room is not at this company: the page marks it External ⚠️ (Mike, 2026-09-20). */
  hasExternal: boolean;
  lastAt: string | null;
  lastSender: string | null;
  lastPreview: string | null;
  messages: number;
  unread: number;
  /** The companies of the people outside the team (pipeline F5, B1957); absent from an older API. */
  companies?: Array<{ id: string; name: string }>;
}

export interface ChatsView {
  store: { tenantId: string; kind: string; name: string };
  chats: ChatRow[];
  counts: { cobrain: number; teams: number; external: number };
  builtAt: string | null;
  emptyBecause: 'nothing_yet' | null;
}

export interface TeamsMessage {
  /** Teams' own id, which is what a reaction is addressed to; null when Cobrain cannot recover it. */
  teamsMessageId: string | null; id: string; sentAt: string; sender: string | null; mine: boolean; body: string | null; external: boolean }

export interface TeamsThreadView {
  threadId: string;
  title: string | null;
  people: ChatPerson[];
  hasExternal: boolean;
  messages: TeamsMessage[];
  more: boolean;
  openInTeams: string | null;
}

export interface TeamsFindView {
  matches: { id: string; sentAt: string; sender: string | null; mine: boolean; snippet: string }[];
  more: boolean;
}

export interface MessageReaction { emoji: string; count: number; names: string[]; mine: boolean }
export interface TeamsReactionsView { reactions: { signalId: string; list: MessageReaction[] }[] }

/** MIRROR of MessageExtra in apps/api/src/chats/pictures.ts, field for field. */
export type AttachmentKind = 'picture' | 'file' | 'card' | 'text' | 'nothing';
export interface MessageExtra {
  signalId: string;
  kind: AttachmentKind;
  pictures: number;
  name: string | null;
  text: string | null;
}

/** MIRROR of ChatMember / ChatRoster in apps/api/src/chats/people.ts. */
export type HistoryChoice = 'none' | 'month' | 'all';
export interface ChatMember {
  membershipId: string | null; name: string | null; address: string | null;
  external: boolean; owner: boolean; you: boolean;
  /** Ringing this one person; null for the reader and for anybody Teams named without an address. */
  call: { audio: string | null; video: string | null };
}
export interface ChatRoster {
  chatType: string | null;
  topic: string | null;
  members: ChatMember[];
  /** Teams itself only lets a GROUP chat's roster change; a one-to-one chat's is fixed. */
  rosterCanChange: boolean;
  /** Teams deep links that ring the people in the room; null when there is nobody with an address to ring. */
  call: { audio: string | null; video: string | null };
  readAt: string;
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json().then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null)).catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}
const get = <T>(path: string, signal?: AbortSignal) =>
  fetch(path, { credentials: 'same-origin', ...(signal ? { signal } : {}) }).then((r) => json<T>(r));
const post = <T>(path: string, body: unknown) => fetch(path, {
  method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
}).then((r) => json<T>(r));
const q = (store: string | null) => (store ? `?store=${encodeURIComponent(store)}` : '');

/** Somebody he can put in a new group: a colleague he has actually been in a Teams conversation with. */
export interface Colleague { name: string; address: string; lastSeen: string | null }
export interface DirectoryAddress { address: string; inDirectory: boolean; name?: string; upn?: string; guest?: boolean }
/** What the API says back when a group is made. `sent` false means the room exists and is empty. */
export interface StartedGroup {
  chatId: string; topic: string | null; members: number; sent: boolean; error?: string;
  /** The conversation here when Cobrain already holds the chat (a one-to-one chat that existed, BUGS.md M58); null otherwise. */
  threadId?: string | null;
}

export const chatsApi = {
  // The signal is the live-refresh policy's (apps/portal/src/live.ts): walking away from the page, or a
  // read that outstays its deadline, ends the request rather than leaving it running behind nobody.
  list: (store: string | null, signal?: AbortSignal) => get<ChatsView>(`/api/chats${q(store)}`, signal),
  teamsThread: (store: string | null, threadId: string) => get<TeamsThreadView>(`/api/chats/teams/${threadId}${q(store)}`),
  // Acting on a Teams conversation from here (M16 lifted, 2026-09-20). Each is one press, one call:
  // nothing outward is sent that the person did not ask for (D69).
  teamsReply: (store: string | null, threadId: string, text: string, html?: string) =>
    post<{ sent: true; id: string; sentAt: string | null }>(`/api/chats/teams/${threadId}/reply${q(store)}`, { text, ...(html === undefined ? {} : { html }) }),
  teamsReact: (store: string | null, threadId: string, messageId: string, reaction: string | { emoji: string }, on: boolean) =>
    post<{ on: boolean }>(`/api/chats/teams/${threadId}/react${q(store)}`, { messageId, ...(typeof reaction === 'string' ? { reaction } : reaction), on }),
  teamsReactions: (store: string | null, threadId: string) =>
    get<TeamsReactionsView>(`/api/chats/teams/${threadId}/reactions${q(store)}`),
  teamsImage: (store: string | null, threadId: string, image: { text: string; contentType: string; base64: string }) =>
    post<{ sent: true; id: string | null }>(`/api/chats/teams/${threadId}/image${q(store)}`, image),
  teamsFile: (store: string | null, threadId: string, file: File, text: string) =>
    fetch(`/api/chats/teams/${threadId}/file${q(store)}`, {
      method: 'POST', credentials: 'same-origin', body: file,
      headers: { 'content-type': 'application/octet-stream', 'x-file-name': encodeURIComponent(file.name),
        ...(text ? { 'x-text': encodeURIComponent(text) } : {}) },
    }).then(r => json<{ sent: true; id: string | null; name: string; notShared: string[] }>(r)),
  // What the messages showing nothing actually are: a picture, a shared file, or words the ingest lost
  // (B1451). Asked for when a conversation opens and when a new message lands -- NOT on the thread's
  // ten-second beat, because behind it is a Graph call.
  teamsExtras: (store: string | null, threadId: string) =>
    get<{ extras: MessageExtra[] }>(`/api/chats/teams/${threadId}/extras${q(store)}`),
  /** Where one picture of one message is drawn from. By POSITION, never by a Teams id: see pictures.ts. */
  teamsPictureUrl: (store: string | null, threadId: string, signalId: string, index: number) =>
    `/api/chats/teams/${threadId}/picture/${signalId}/${index}${q(store)}`,
  // Who is in the room now. A READ, on a permission Cobrain already holds: it is not behind the consent
  // that replying is behind, and it sends nothing.
  teamsPeople: (store: string | null, threadId: string) => get<ChatRoster>(`/api/chats/teams/${threadId}/people${q(store)}`),
  teamsFind: (store: string | null, threadId: string, words: string) =>
    get<TeamsFindView>(`/api/chats/teams/${threadId}/find${q(store)}${store ? '&' : '?'}q=${encodeURIComponent(words)}`),
  teamsRename: (store: string | null, threadId: string, topic: string) =>
    post<unknown>(`/api/chats/teams/${threadId}/topic${q(store)}`, { topic }),
  // Starting a group (B1223). The colleagues are a read of his own conversations, not a company
  // directory: see colleaguesToPick for why a directory is the wrong list.
  teamsColleagues: (store: string | null) => get<{ colleagues: Colleague[] }>(`/api/chats/teams-colleagues${q(store)}`),
  teamsDirectory: (store: string | null, address: string) =>
    get<DirectoryAddress>(`/api/chats/teams-directory${q(store)}${store ? '&' : '?'}address=${encodeURIComponent(address)}`),
  // The one-to-one chat with this colleague that Cobrain already holds, or null (BUGS.md M58).
  teamsDirect: (store: string | null, address: string) =>
    get<{ threadId: string | null }>(`/api/chats/teams-direct${q(store)}${store ? '&' : '?'}address=${encodeURIComponent(address)}`),
  // The first message is required and the API says so: a Teams group with nothing said in it never
  // appears, here or in Teams, for anybody.
  teamsStartGroup: (store: string | null, body: { topic: string; addresses: string[]; text: string; html?: string }) =>
    post<StartedGroup>(`/api/chats/teams-group${q(store)}`, body),
  // Changing the roster. Each is one press, as everything outward on this page is (D69), and an add
  // always carries the history choice: the API refuses rather than choosing it for anybody.
  teamsAddMember: (store: string | null, threadId: string, address: string, history: HistoryChoice) =>
    post<{ added: true }>(`/api/chats/teams/${threadId}/members${q(store)}`, { address, history }),
  teamsRemoveMember: (store: string | null, threadId: string, membershipId: string) =>
    post<{ removed: true }>(`/api/chats/teams/${threadId}/members/remove${q(store)}`, { membershipId }),
  consent: () => get<TeamsConsentStatus>('/api/teams-consent'),
};
